import { z } from 'zod'
import { namedOutlinesProblem, outlineNameKey } from './furniture-area.dto.js'

/**
 * A landmark: a named outline on a room's drawing marking something people
 * refer to by name — "the stairs", "the bar", "the fireplace" — so an assistant
 * asked to put a chair near one knows where it is. See `docs/landmarks.md`.
 *
 * Drawn and named like a furniture area, but kept apart from the areas on
 * purpose: the areas together are the floor furniture is held to, and a
 * landmark holds nothing. Mixed into one list, every reader of the floor would
 * have to leave the landmarks out, and the one that forgot would make the
 * stairs part of the floor.
 *
 * `points` is the closed outline, `[x0, y0, x1, y1, …]` in the room's design
 * pixels (an inch each).
 */

/** The longest a landmark's name may be. */
export const LANDMARK_NAME_MAX = 60

export const landmarkSchema = z.object({
  id: z.string().min(1),
  /** What people, and an assistant, call it: "Stairs", "Bar". */
  name: z.string().trim().min(1, 'Give the landmark a name.').max(LANDMARK_NAME_MAX),
  points: z
    .array(z.number())
    .min(6, 'A landmark needs at least three corners.')
    .refine((points) => points.length % 2 === 0, 'Every corner needs both coordinates.'),
})

export type Landmark = z.infer<typeof landmarkSchema>

/**
 * A room's landmarks as stored and sent, read as they are without the checks a
 * save makes, so one that would no longer pass them still loads.
 */
export const storedLandmarksSchema = z.array(landmarkSchema)

/**
 * A room's landmarks, as a save sends them: names unique within the room,
 * ignoring case, and no outline crossing itself, as for furniture areas.
 */
export const landmarksSchema = z.array(landmarkSchema).superRefine((landmarks, ctx) => {
  const problem = namedOutlinesProblem(landmarks, 'landmarks')
  if (problem !== null) ctx.addIssue({ code: 'custom', message: problem })
})

/**
 * A name an area and a landmark of one room share, ignoring case, or null when
 * none is shared. An assistant refers to both by name, so "the bar" has to mean
 * one thing.
 */
export function nameSharedByAreaAndLandmark(
  areas: ReadonlyArray<{ name: string }>,
  landmarks: ReadonlyArray<{ name: string }>,
): string | null {
  const areaNames = new Set(areas.map((a) => outlineNameKey(a.name)))
  const shared = landmarks.find((l) => areaNames.has(outlineNameKey(l.name)))
  return shared ? shared.name.trim() : null
}
